import { z } from 'zod';

export interface TransformationValidationError {
  mappingKey: string;
  resourceField?: string;
  message: string;
}

export interface TransformationValidationResult {
  isValid: boolean;
  errors: TransformationValidationError[];
}

export const RESOURCE_GATEWAY_FIELD_ATTRIBUTES = ['UpsertKey', 'Required'] as const;
export type ResourceGatewayFieldAttribute = (typeof RESOURCE_GATEWAY_FIELD_ATTRIBUTES)[number];

export type ResourceGatewayMappingSource =
  | { type: 'field'; field: string }
  | { type: 'constant'; value: string }
  | { type: 'template'; template: string };

export const ResourceGatewayMappingSourceSchema: z.ZodType<ResourceGatewayMappingSource> = z.union([
  z.object({ type: z.literal('field'), field: z.string() }).strict(),
  z.object({ type: z.literal('constant'), value: z.string() }).strict(),
  z.object({ type: z.literal('template'), template: z.string() }).strict(),
]);

const TransformStepSchema = z
  .object({
    id: z.string().optional(),
    function: z.string(),
    options: z.record(z.string(), z.string()).optional(),
  })
  .strict();

const FieldMappingSchema = z
  .object({
    resourceField: z.string(),
    source: ResourceGatewayMappingSourceSchema,
    transforms: z.array(TransformStepSchema).optional(),
    attributes: z.array(z.enum(RESOURCE_GATEWAY_FIELD_ATTRIBUTES)).optional(),
  })
  .strict();

const ResourceMappingSchema = z
  .object({
    key: z.string(),
    resourceKey: z.string(),
    fieldMappings: z.array(FieldMappingSchema),
  })
  .strict();

export const TransformationDefinitionSchema = z
  .object({
    version: z.number().int().positive().optional(),
    resourceMappings: z.array(ResourceMappingSchema),
  })
  .strict();

export type TransformationDefinition = z.infer<typeof TransformationDefinitionSchema>;

export interface Transformation {
  id: string;
  organizationId: string;
  name: string;
  transformationDefinition: TransformationDefinition;
  created: string;
  updated: string;
}

export interface CreateTransformationOptions {
  organizationId: string;
  name: string;
  transformationDefinition: TransformationDefinition;
}

export interface TransformationFeed {
  id: string;
  created: string;
  transformationId: string;
  feedId: string;
}

export interface TransformationFeedOptions {
  organizationId: string;
  transformationId: string;
  feedId: string;
}

export interface FindAllTransformationsOptions {
  organizationId?: string;
  limit?: number;
  offset?: number;
}

export interface FindAllTransformationsResponse {
  transformations: Transformation[];
  total: number;
  limit: number;
  offset: number;
}

export interface FindByIdOptions {
  transformationId: string;
}

export interface DeleteTransformationOptions {
  organizationId: string;
  transformationId: string;
}

export interface FindAllLinkedFeedsOptions {
  organizationId: string;
  transformationId: string;
}

export interface FindAllLinkedFeedsResponse {
  transformationFeeds: TransformationFeed[];
}
